/*
 * The two fixed message templates a lead task offers in place of AI
 * suggestions.
 *
 * Antaryami builds its prompt from a student id, and a lead has no student row
 * at all, so "Generate suggestions" cannot run on these tasks (Q7). Two written
 * openers keep the outbound half of the lead task usable without leaving the
 * agent on a blank box.
 *
 * Pure so the copy is testable: form.jsx only renders what comes back.
 *
 * ASCII only, apostrophes included: these go out over SMS, and one typographic
 * character drops the encoding to UCS-2, which halves a segment to 70
 * characters.
 */

import { withOnboardingLink } from './onboarding_link';

// "Asha Mehta" → "Asha". Everything the templates need is the first token; a
// single-word name is its own first name.
export const firstName = (fullName: string | null | undefined): string =>
  String(fullName || '')
    .trim()
    .split(/\s+/)[0] || '';

// A lead gave a name on the landing page, but it can be blank or junk — the
// greeting still has to read as a greeting.
const studentFirst = (name: string): string => firstName(name) || 'there';

/*
 * Two options, in the order they render. `text` is ready to drop into the
 * textarea: the onboarding-link footer is already applied (and is a no-op when
 * the server sent no link, which is why this never has to branch on it).
 */
export interface LeadTemplateInput {
  student_name?: string;
  agent_name?: string;
  onboarding_link?: string;
}

export interface LeadTemplate {
  key: string;
  label: string;
  text: string;
}

export const lead_message_templates = ({
  student_name: studentName = '',
  agent_name: agentName = '',
  onboarding_link: onboardingLink = ''
}: LeadTemplateInput = {}): LeadTemplate[] => {
  const who = studentFirst(studentName);
  const agent = firstName(agentName);
  // Without a signed-in agent name the sentence still has to stand on its own
  // rather than read "this is  from MyYogaTeacher".
  const intro = agent ? `this is ${agent} from MyYogaTeacher` : 'this is the MyYogaTeacher team';
  return [
    {
      key: 'unfinished_signup',
      label: "Didn't finish signing up",
      text: withOnboardingLink(
        `Hi ${who}, ${intro}. I saw you started signing up for your free session but didn't get to finish. Happy to help you pick a time that works.`,
        onboardingLink
      )
    },
    {
      key: 'hold_a_slot',
      label: 'Hold a slot',
      text: withOnboardingLink(
        `Hi ${who}, it takes under a minute to finish setting up and book your free 1-on-1 yoga session. Want me to hold a slot for you?`,
        onboardingLink
      )
    }
  ];
};
